import { StyleSheet, Text, View } from "@react-pdf/renderer";
import React from "react";

export const PAGE_WIDTH = 576;
const UNPRINTABLE_MARGIN = 18;
export const VERTICAL_GAP = 12;
const HORIZONTAL_GAP = 12;
const FONT_SIZE = 9;
const BORDER_WIDTH = 0.5;
export const STANDARD_LINE = `${BORDER_WIDTH} solid black`;
export const styles = StyleSheet.create({
    pageStyle: {
        paddingTop: 16,
        paddingHorizontal: 16,
        paddingBottom: 36,
        fontSize: FONT_SIZE,
        fontFamily: "Roboto",
    },
    topCell: {
        borderTopWidth: BORDER_WIDTH,
    },
    rightCell: {
        borderRightWidth: BORDER_WIDTH,
    },
    bottomCell: {
        borderBottomWidth: BORDER_WIDTH,
    },
    leftCell: {
        borderLeftWidth: BORDER_WIDTH,
    },
    regularCell: {},

    sectionHeader: {
        fontSize: FONT_SIZE * 1.3,
        backgroundColor: "#B1FF53FF", // TODO these need to come from a palette or something
        marginTop: HORIZONTAL_GAP,
        padding: "3 2 3 2",
        borderTop: STANDARD_LINE,
        borderBottom: STANDARD_LINE,
    },

    columnHeader: {
        fontSize: FONT_SIZE * 1.1,
        padding: 2,
    },

    columnHeaderRightJustified: {
        fontSize: FONT_SIZE * 1.1,
        padding: 2,

        textAlign: "right",
    },

    gridLabelCell: {
        padding: 2,
    },

    gridLabelCellRightJustified: {
        padding: 2,
        textAlign: "right",
    },

    gridCell: {
        fontWeight: 700,
        padding: 2,
    },

    gridCellRightJustified: {
        fontWeight: 700,
        padding: 2,

        textAlign: "right",
    },

    footerSection: {
        position: "absolute",
        bottom: UNPRINTABLE_MARGIN,
        left: UNPRINTABLE_MARGIN,
        right: 0,
        marginTop: 54,
        flexDirection: "row",
        alignItems: "flex-end",
    },

    footerLeft: {
        fontSize: 7,
        textAlign: "left",
    },

    footerCenter: {
        fontSize: 6,
        textAlign: "center",
    },

    footerRight: {
        fontSize: 7,
        textAlign: "right",
    },

    reportSection: {
        marginBottom: HORIZONTAL_GAP,
    },

    gridHeader: {
        borderBottom: STANDARD_LINE,
    },

    gridFooter: {
        marginTop: -HORIZONTAL_GAP,
        borderTop: STANDARD_LINE,
    },

    oddRow: {
        backgroundColor: "#00000010",
    },

    oddColumn: {
        backgroundColor: "#00000010",
    },

    oddRowAndColumn: {
        backgroundColor: "#00000020",
    },

    nthColumn: {
        borderLeft: STANDARD_LINE,
    },

    oddRowAndNthColumn: {
        backgroundColor: "#00000010",
        borderLeft: STANDARD_LINE,
    },

    corporateAddress: { textAlign: "center", fontSize: 10 },
    ticketNumber: { fontWeight: "bold", fontSize: 10, textAlign: "right" },
    ticketNumberLabel: { fontSize: 9, textAlign: "right" },

    lastAddressRow: { marginBottom: 18 },

    flightLegsFooter: {
        marginTop: -6,
        borderTop: "1px solid black",
        padding: 2,
        textAlign: "right",
    },

    customerStamp: {
        height: 108,
        border: STANDARD_LINE,
    },
});

export function calculateColumnWidths(rowWidth: number, relativeWidths: number[]): number[] {
    const columnWidthsInPoints: number[] = [];
    const percentageBase = relativeWidths.reduce((v, c) => {
        return (v += c);
    });

    relativeWidths.forEach((relativeWidth) => {
        columnWidthsInPoints.push((rowWidth * relativeWidth) / percentageBase);
    });

    const allocatedWidth = columnWidthsInPoints.reduce((v, c) => {
        return (v += c);
    });

    const roundingAdjustment = rowWidth - allocatedWidth;
    columnWidthsInPoints[0] += roundingAdjustment;
    return columnWidthsInPoints;
}

export function createGridColumnStyles(rowWidth: number, relativeWidths: number[], baseStyles: Record<string, unknown>[]): Record<string, unknown>[] {
    const columnWidthsInPoints = calculateColumnWidths(rowWidth, relativeWidths);

    const stylesWithWidths = baseStyles.map((s, i) => {
        return { ...s, width: columnWidthsInPoints[i] };
    });
    return stylesWithWidths;
}

export const renderSectionHeader = (width: number, rootKey: string, sectionTitle: string): JSX.Element => {
    return (
        <View key={rootKey + "sh"} style={{ flexDirection: "row" }}>
            <Text style={{ ...styles.sectionHeader, width: width }}>{sectionTitle}</Text>
        </View>
    );
};

export function renderGrid(
    rootKey: string,
    width: number,
    cellDecorator: (rowIndex: number, columnIndex: number) => Record<string, unknown>,
    columnWidths: number[],
    columnStyles: Record<string, unknown>[],
    data: string[][],
    containerStyle: Record<string, unknown> = styles.reportSection
): JSX.Element {
    const sizedStyles = createGridColumnStyles(width, columnWidths, columnStyles);

    return (
        <View key={rootKey + "g"} style={{ flexDirection: "row" }}>
            <View key={rootKey + "gg"} style={containerStyle}>
                {renderGridRows(rootKey + "gg", width, cellDecorator, sizedStyles, data)}
            </View>
        </View>
    );
}

function renderGridRows(
    gridKey: string,
    rowWidth: number,
    cellDecorator: (rowIndex: number, columnIndex: number) => Record<string, unknown>,
    columnStyles: Record<string, unknown>[],
    data: string[][]
) {
    const result: JSX.Element[] = [];

    data.forEach((rowOfData, rowIndex) => {
        const row: JSX.Element[] = [];
        const rowKey = gridKey + "g" + rowIndex;

        rowOfData.forEach((column, columnIndex) => {
            row.push(
                <Text key={rowKey + columnIndex} style={{ ...columnStyles[columnIndex], ...cellDecorator(rowIndex, columnIndex) }}>
                    {column}
                </Text>
            );
        });

        result.push(
            <View key={rowKey} style={{ flexDirection: "row" }} wrap={false}>
                {row}
            </View>
        );
    });

    return result;
}

export function highlightEvenRowsAndOddColumns(rowIndex: number, columnIndex: number): Record<string, unknown> {
    const isOddRow = rowIndex % 2 == 1;
    const isOddColumn = columnIndex % 2 == 1;
    if (!isOddRow) {
        return isOddColumn ? styles.oddRowAndColumn : styles.oddRow;
    }
    return isOddColumn ? styles.oddColumn : {};
}

export function highlightOddRowsAndColumns(rowIndex: number, columnIndex: number): Record<string, unknown> {
    const isOddRow = rowIndex % 2 == 1;
    const isOddColumn = columnIndex % 2 == 1;
    if (isOddRow) {
        return isOddColumn ? styles.oddRowAndColumn : styles.oddRow;
    }
    return isOddColumn ? styles.oddColumn : {};
}

export function highlightOddRowsAndColumnLines(rowIndex: number, columnIndex: number): Record<string, unknown> {
    const isOddRow = rowIndex % 2 == 1;
    const isNthColumn = columnIndex > 0;
    if (isOddRow) {
        return isNthColumn ? styles.oddRowAndNthColumn : styles.oddRow;
    }
    return isNthColumn ? styles.nthColumn : {};
}

export function gridHeaderWithColumnLines(rowIndex: number, columnIndex: number): Record<string, unknown> {
    const isNthColumn = columnIndex > 0;
    return isNthColumn ? styles.oddRowAndNthColumn : styles.oddColumn;
}

export function noHighlights(rowIndex: number, columnIndex: number): Record<string, unknown> {
    return {};
}

export function allHighlights(rowIndex: number, columnIndex: number): Record<string, unknown> {
    return styles.oddColumn;
}

export function highlightEvenRows(rowIndex: number, columnIndex: number): Record<string, unknown> {
    return rowIndex % 2 == 0 ? styles.oddRow : {};
}

export function highlightOddRows(rowIndex: number, columnIndex: number): Record<string, unknown> {
    return rowIndex % 2 == 0 ? {} : styles.oddRow;
}
